import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getUserByPgId, updateUserProfileByPgId } from '@/features/users/api';
import { ApiError } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { useProfilePhoto } from '@/hooks/useProfilePhoto';
import { setLanguageFromSession } from '@/i18n';
import { getInitials } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck } from 'lucide-react';
import { PhotoCropper } from '../PhotoCropper';

function formatRoleName(role: string): string {
  return role
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
import { Camera, ImagePlus, X } from 'lucide-react';

interface ProfileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Self-service profile modal — lets the signed-in user edit their own basic
 * contact details (name, phone numbers, position, language) without going
 * through the full admin user form.
 */
export function ProfileModal({ open, onOpenChange }: ProfileModalProps) {
  const { user: session, invalidateSession } = useSession();
  const queryClient = useQueryClient();
  // V4 sessions: prefer pg_id (the PG UUID source of truth). Fall back to
  // _id only if pg_id is somehow missing — but in V4 they're the same value,
  // so this is purely belt-and-braces. The profile MUST be looked up via PG
  // so we don't accidentally resolve a PG UUID through the Mongo filter and
  // surface a different user's data.
  const pgId = session?.pg_id || session?._id;

  // Fetch full user record so we pick up phone/mobile/extension/position etc.
  const userQuery = useQuery({
    queryKey: ['session-profile', pgId],
    queryFn: () => getUserByPgId(pgId as string),
    enabled: open && !!pgId,
    staleTime: 30_000,
  });

  // PG-native self-edit. Writes straight to PostgreSQL via /v4/users/:pgId/profile —
  // no Mongo, no CDC wait. Keeps the modal as the source of truth for fields
  // owned by the users table (firstname/lastname/contact/language/linkedin).
  const updateUserMutation = useMutation({
    mutationFn: ({ pgId: id, data }: { pgId: string; data: Parameters<typeof updateUserProfileByPgId>[1] }) =>
      updateUserProfileByPgId(id, data),
    onError: (error) => {
      const description = error instanceof ApiError
        ? (error.message || `Request failed (${error.status})`)
        : error instanceof Error ? error.message : 'Unknown error';
      toast.error('Failed to update profile', { description });
    },
  });

  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [phone, setPhone] = useState('');
  const [extension, setExtension] = useState('');
  const [mobile, setMobile] = useState('');
  const [position, setPosition] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [linkedinEmail, setLinkedinEmail] = useState('');
  const [linkedinPassword, setLinkedinPassword] = useState('');
  const [linkedinPasswordSet, setLinkedinPasswordSet] = useState(false);
  const [language, setLanguage] = useState<'en' | 'fr'>('en');
  const [savedPhoto, setSavedPhoto] = useProfilePhoto(pgId);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [cropSource, setCropSource] = useState<File | null>(null);
  const [cameraActive, setCameraActive] = useState(false);

  // Fall-back chain: just-cropped preview (this modal session) → localStorage
  // cache (this browser) → the persisted GCS URL from PG (cross-device source
  // of truth). Without the PG fallback the photo would only appear on the
  // browser that originally uploaded it.
  const displayedPhoto = photoPreview ?? savedPhoto ?? userQuery.data?.profile_photo_url ?? null;
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraActive(false);
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Camera not available in this browser');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 640 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraActive(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to access camera';
      toast.error('Camera access denied', { description: message });
    }
  };

  // Attach the stream to the <video> once it renders, and clean up on unmount/close.
  useEffect(() => {
    if (cameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => { /* autoplay blocked */ });
    }
  }, [cameraActive]);

  useEffect(() => () => stopCamera(), []);
  useEffect(() => { if (!open) stopCamera(); }, [open]);

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const size = Math.min(video.videoWidth, video.videoHeight);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Center-crop to a square, mirror so the preview matches the user's view.
    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;
    ctx.translate(size, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
    setPhotoPreview(canvas.toDataURL('image/jpeg', 0.9));
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const filename = `profile-${pgId ?? 'me'}-${Date.now()}.jpg`;
        setPhotoFile(new File([blob], filename, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.9,
    );
    stopCamera();
  };

  // Seed the form when the modal opens / the user record lands.
  useEffect(() => {
    if (!open) return;
    const u = userQuery.data;
    if (!u) return;
    setFirstname(u.firstname ?? '');
    setLastname(u.lastname ?? '');
    setPhone(u.phone ?? '');
    setExtension(u.extension ?? '');
    setMobile(u.mobile ?? '');
    setPosition(u.position ?? '');
    setLinkedin(u.linkedin ?? '');
    setLinkedinEmail(u.linkedin_email ?? '');
    setLinkedinPassword('');
    setLinkedinPasswordSet(Boolean(u.linkedin_password_set));
    setLanguage((u.language as 'en' | 'fr') ?? 'en');
    setPhotoFile(null);
    // Clear any in-flight preview; the persisted photo (from the hook) handles display.
    setPhotoPreview(null);
  }, [open, userQuery.data]);

  const handlePhotoPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Kick off the cropper — photoFile is only set after the user applies.
    setCropSource(file);
    // Reset input so picking the same file twice still triggers onChange.
    e.target.value = '';
  };

  const handleCropApply = (cropped: File) => {
    setPhotoFile(cropped);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(typeof reader.result === 'string' ? reader.result : null);
    reader.readAsDataURL(cropped);
    setCropSource(null);
  };

  /**
   * Upload to the public atek-prod-files bucket (NOT DMS) so the resulting
   * storage.googleapis.com URL can be embedded directly in email signatures
   * without signed-URL expiry or a proxy hop.
   */
  const uploadPhotoToGcs = async (file: File): Promise<{ publicUrl: string } | null> => {
    try {
      const form = new FormData();
      form.append('file', file);
      const resp = await fetch('/api/v4/profile-photos/upload', {
        method: 'POST',
        body: form,
        credentials: 'include',
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `HTTP ${resp.status}`);
      }
      const data = (await resp.json()) as { url: string };
      return { publicUrl: data.url };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      toast.error('Photo upload failed', { description: message });
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pgId) return;
    let uploadedPhotoUrl: string | null = null;
    if (photoFile) {
      const result = await uploadPhotoToGcs(photoFile);
      if (result) {
        toast.success('Photo uploaded');
        uploadedPhotoUrl = result.publicUrl;
        if (photoPreview) setSavedPhoto(photoPreview);
      }
    }
    await updateUserMutation.mutateAsync({
      pgId,
      data: {
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        phone: phone.trim(),
        extension: extension.trim(),
        mobile: mobile.trim(),
        position: position.trim(),
        linkedin: linkedin.trim(),
        linkedin_email: linkedinEmail.trim(),
        ...(linkedinPassword ? { linkedin_password: linkedinPassword } : {}),
        language,
        ...(uploadedPhotoUrl ? { profile_photo_url: uploadedPhotoUrl } : {}),
      },
    });
    toast.success('Profile updated');
    setLanguageFromSession(language);
    invalidateSession();
    queryClient.invalidateQueries({ queryKey: ['session-profile', pgId] });
    onOpenChange(false);
  };

  const fullName = `${firstname} ${lastname}`.trim() || session?.email || '';
  const isLoading = userQuery.isLoading && !userQuery.data;
  const roles = session?.postgres_roles ?? [];
  const permissions = session?.permission_strings ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>My Profile</DialogTitle>
          <DialogDescription>Update your personal contact details.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {cameraActive ? (
            <div className="flex flex-col items-center gap-3">
              <div className="relative h-48 w-48 rounded-full overflow-hidden bg-black ring-2 ring-blue-200">
                <video
                  ref={videoRef}
                  playsInline
                  muted
                  className="h-full w-full object-cover"
                  style={{ transform: 'scaleX(-1)' }}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button type="button" onClick={capturePhoto} size="sm">
                  <Camera className="h-4 w-4 mr-1.5" />
                  Capture
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={stopCamera}>
                  <X className="h-4 w-4 mr-1.5" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <Avatar className="h-20 w-20">
                {displayedPhoto && <AvatarImage src={displayedPhoto} alt={fullName} />}
                <AvatarFallback className="text-lg bg-blue-100 text-blue-700">
                  {getInitials(fullName || 'U')}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 cursor-pointer">
                    <ImagePlus className="h-4 w-4" />
                    <span>Upload</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePhotoPick}
                    />
                  </label>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={startCamera}
                    className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"
                  >
                    <Camera className="h-4 w-4" />
                    <span>Take photo</span>
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Saved to ATEK cloud storage when you click Save changes.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="profile-firstname">First name</Label>
              <Input
                id="profile-firstname"
                value={firstname}
                onChange={(e) => setFirstname(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="profile-lastname">Last name</Label>
              <Input
                id="profile-lastname"
                value={lastname}
                onChange={(e) => setLastname(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Email</Label>
            <Input value={session?.email ?? ''} disabled />
          </div>

          <div className="grid grid-cols-[1fr_96px] gap-3">
            <div className="space-y-1">
              <Label htmlFor="profile-phone">Phone</Label>
              <Input
                id="profile-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 514 555 0100"
                disabled={isLoading}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="profile-extension">Ext.</Label>
              <Input
                id="profile-extension"
                value={extension}
                onChange={(e) => setExtension(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="profile-mobile">Mobile</Label>
            <Input
              id="profile-mobile"
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="profile-position">Position</Label>
              <Input
                id="profile-position"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="profile-linkedin">LinkedIn URL</Label>
              <Input
                id="profile-linkedin"
                value={linkedin}
                onChange={(e) => setLinkedin(e.target.value)}
                placeholder="https://linkedin.com/in/..."
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2.5">
            <div className="text-sm font-medium text-slate-700">
              LinkedIn automation credentials
              <span className="ml-2 text-[10px] font-normal text-slate-500 uppercase tracking-wide">
                used by apps/linkedin bot
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="profile-linkedin-email">LinkedIn email</Label>
                <Input
                  id="profile-linkedin-email"
                  type="email"
                  autoComplete="off"
                  value={linkedinEmail}
                  onChange={(e) => setLinkedinEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="profile-linkedin-password">LinkedIn password</Label>
                <Input
                  id="profile-linkedin-password"
                  type="password"
                  autoComplete="new-password"
                  value={linkedinPassword}
                  onChange={(e) => setLinkedinPassword(e.target.value)}
                  placeholder={linkedinPasswordSet ? '•••••••• (saved — leave blank to keep)' : 'Enter password'}
                  disabled={isLoading}
                />
                <p className="text-[10px] text-slate-500">
                  {linkedinPasswordSet ? 'A password is already saved. Blank = keep existing.' : 'Not set.'}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <Label>Language</Label>
            <Select
              value={language}
              onValueChange={(v) => setLanguage(v as 'en' | 'fr')}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2.5">
            <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
              <ShieldCheck className="h-4 w-4 text-blue-600" />
              <span>Privileges</span>
            </div>

            <div className="space-y-1">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Roles</div>
              {roles.length === 0 ? (
                <div className="text-xs text-gray-400 italic">No roles assigned</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {roles.map((role) => (
                    <Badge key={role} variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                      {formatRoleName(role)}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Permissions {permissions.length > 0 && <span className="text-gray-400">({permissions.length})</span>}
              </div>
              {permissions.length === 0 ? (
                <div className="text-xs text-gray-400 italic">No individual permissions</div>
              ) : (
                <div className="max-h-48 overflow-y-auto pr-1 rounded border border-gray-200 bg-white p-2">
                  <div className="flex flex-wrap gap-1">
                    {permissions.map((perm) => (
                      <Badge key={perm} variant="outline" className="font-mono text-[10px] py-0 px-1.5">
                        {perm}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateUserMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || updateUserMutation.isPending}>
              {updateUserMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      <PhotoCropper
        file={cropSource}
        open={!!cropSource}
        onApply={handleCropApply}
        onCancel={() => setCropSource(null)}
      />
    </Dialog>
  );
}
